import {
    Box,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
    alpha
} from '@mui/material';
import ResponsiveTooltip from '../ResponsiveTooltip';
import { useTranslation } from '../../hooks/useTranslation';
import React, { useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useFormatter } from '../../context/FormatterContext';
import { TimeRange, useDashboardData } from '../../hooks/useDashboardData';



interface BalanceHistoryChartProps {
    balance?: number;
}

const BalanceHistoryChart: React.FC<BalanceHistoryChartProps> = ({ balance }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const { formatCurrency, formatDateShort, formatPercentage } = useFormatter();
    const {
        timeRange,
        setTimeRange,
        timeRangePortfolioHistory: chartData,
        isTimeRangeHistoryLoading: isLoading,
        assetsData
    } = useDashboardData();

    // Calculate 24h performance sum
    const performance24h = useMemo(() => {
        if (!assetsData || assetsData.length === 0) return 0;
        
        // Sum up the weighted 24h performance values
        let totalValue = 0;
        let totalWeightedChange = 0;
        
        assetsData.forEach(asset => {
            totalValue += asset.value;
            // Check if price_trend1 exists and use it safely
            if ('price_trend1' in asset && typeof asset.price_trend1 === 'number') {
                totalWeightedChange += (asset.value * asset.price_trend1) / 100;
            }
        });
        
        // Calculate percentage change
        return totalValue > 0 ? (totalWeightedChange / totalValue) * 100 : 0;
    }, [assetsData]);

    const handleTimeRangeChange = (_: React.MouseEvent<HTMLElement>, newTimeRange: TimeRange | null) => {
        if (newTimeRange !== null) {
            setTimeRange(newTimeRange);
        }
    };

    const fullData = chartData || [];

    // Custom tick component for X-axis
    const CustomXAxisTick = (props: any) => {
        const { x, y, payload } = props;
        
        // Handle different timestamp formats
        let formattedValue;
        if (timeRange === 'ALL') {
            // Ensure the timestamp is properly converted to a date
            const timestamp = typeof payload.value === 'number' ? payload.value : parseInt(payload.value, 10);
            // Check if the timestamp needs multiplication by 1000 (if it's in seconds, not milliseconds)
            const date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
            formattedValue = date.toLocaleDateString(undefined, { 
                month: 'short',
                year: 'numeric' 
            });
        } else {
            formattedValue = formatDateShort(payload.value);
        }

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    dy={16}
                    textAnchor="middle"
                    fontSize={12}
                    fill={theme.palette.text.secondary}
                >
                    {formattedValue}
                </text>
            </g>
        );
    };

    // Custom tick component for Y-axis
    const CustomYAxisTick = (props: any) => {
        const { x, y, payload } = props;
        const formattedValue = formatCurrency(payload.value);

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    dx={-10}
                    dy={4}
                    textAnchor="end"
                    fontSize={12}
                    fill={theme.palette.text.secondary}
                >
                    {formattedValue}
                </text>
            </g>
        );
    };

    // Custom tooltip component for ensuring readability in dark mode
    const CustomResponsiveTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            // Fix timestamp handling to avoid incorrect year display
            let formattedDate;
            try {
                // Check if the timestamp needs to be multiplied by 1000 (if it's in seconds, not milliseconds)
                const timestamp = typeof label === 'number' ? label : parseInt(label, 10);
                const date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
                formattedDate = date.toLocaleDateString(undefined, { 
                    day: 'numeric',
                    month: 'long'
                });
            } catch (error) {
                console.error('Error formatting date:', error);
                formattedDate = label;
            }
            
            const formattedValue = formatCurrency(payload[0].value);

            return (
                <Box sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    padding: 1.5,
                    boxShadow: theme.shadows[2],
                }}>
                    <Typography 
                        variant="caption" 
                        component="div" 
                        sx={{
                            color: theme.palette.text.secondary,
                            marginBottom: 0.5,
                        }}
                    >
                        {formattedDate}
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {formattedValue}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Box
            sx={{
                p: 1,
                pt: 1.5,
                height: '100%',
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            }}
        >
            {/* Desktop header with balance and time controls side by side */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    mt: 2.5,
                    flexDirection: isMediumScreen ? 'column' : 'row',
                    gap: isMediumScreen ? 2 : 0,
                }}
            >
                {isLoading ? (
                    <Skeleton variant="text" width={150} height={40} sx={{ position: isMediumScreen ? 'relative' : 'absolute', left: isMediumScreen ? 'auto' : 10 }} />
                ) : (
                    <Typography
                        sx={{
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            position: isMediumScreen ? 'relative' : 'absolute',
                            left: isMediumScreen ? 'auto' : 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        {balance && formatCurrency(balance)}
                        {performance24h !== 0 && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 'bold',
                                    color: '#FFFFFF',
                                    bgcolor: performance24h >= 0 ? '#15B834' : '#D32F2F',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    display: 'inline-block',
                                    ml: 1
                                }}
                            >
                                {performance24h >= 0 ? '+' : ''}{formatPercentage(performance24h)}
                            </Typography>
                        )}
                    </Typography>
                )}

                {/* Time controls - only visible on non-mobile */}
                <Box
                    sx={{
                        mr: 1.5,
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                        ml: isMediumScreen ? 'auto' : 'auto',
                        mt: isMediumScreen ? 0 : 0,
                        alignSelf: isMediumScreen ? 'flex-end' : 'auto',
                    }}
                >
                    <ToggleButtonGroup
                        value={timeRange}
                        exclusive
                        onChange={handleTimeRangeChange}
                        size="small"
                        disabled={isLoading}
                        sx={{
                            border: '1px solid #333333',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            '& .MuiToggleButtonGroup-grouped': {
                                margin: 0,
                                border: 0,
                                borderRadius: 0,
                                borderRight: `1px solid #333333`,
                                '&:last-of-type': {
                                    borderRight: 0,
                                },
                            },
                        }}
                    >
                        <ToggleButton 
                            value="1W" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1W')}
                        </ToggleButton>
                        <ToggleButton 
                            value="1M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="3M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.3M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="6M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.6M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="1Y" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1Y')}
                        </ToggleButton>
                        <ToggleButton 
                            value="ALL" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.ALL')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Box sx={{ 
                flex: 1,
                minHeight: 280, 
                width: '100%',
                position: 'relative',
                mt: 2,
                px: 0,
            }}>
                {isLoading ? (
                    <Box
                        width="100%"
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        {/* Chart skeleton */}
                        <Box display="flex" alignItems="flex-end" height="100%" width="100%" position="relative">
                            {/* Y-axis skeleton */}
                            {!isMobile && (
                                <Box
                                    position="absolute"
                                    left={0}
                                    height="100%"
                                    width={90}
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="space-between"
                                    pt={2}
                                    pb={2}
                                >
                                    {[...Array(5)].map((_, i) => (
                                        <Skeleton key={i} variant="text" width={60} height={20} />
                                    ))}
                                </Box>
                            )}

                            {/* Chart line skeleton */}
                            <Box
                                width="100%"
                                height="100%"
                                display="flex"
                                alignItems="center"
                                pl={!isMobile ? 11 : 1}
                                pr={1}
                            >
                                <Skeleton variant="rounded" width="100%" height="70%" />
                            </Box>
                        </Box>

                        {/* X-axis skeleton */}
                        <Box display="flex" justifyContent="space-between" mt={1} ml={!isMobile ? 11 : 1} mr={1}>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} variant="text" width={40} height={20} />
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <ResponsiveContainer>
                        <AreaChart 
                            data={fullData} 
                            margin={{ 
                                top: 5, 
                                right: 10, 
                                left: 0, 
                                bottom: 0 
                            }}
                        >
                            <XAxis
                                dataKey="date"
                                tick={<CustomXAxisTick />}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={30}
                            />
                            {!isMobile && (
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={<CustomYAxisTick />}
                                    width={105}
                                    domain={['auto', 'auto']}
                                    orientation="left"
                                    allowDecimals={false}
                                    padding={{ top: 10 }}
                                    tickCount={5}
                                />
                            )}
                            <ResponsiveTooltip content={<CustomResponsiveTooltip />} />
                            <defs>
                                <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#ff4900" stopOpacity={0.9}/>
                                    <stop offset="50%" stopColor="#ff4900" stopOpacity={0.4}/>
                                    <stop offset="100%" stopColor="#ff4900" stopOpacity={0.2}/>
                                </linearGradient>
                            </defs>
                            <Area
                                type="monotone"
                                dataKey="balance"
                                stroke="#ff4900"
                                strokeWidth={2}
                                fill="url(#colorBalance)"
                                fillOpacity={1}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </Box>

            {/* Mobile time controls - only visible on mobile, positioned below chart */}
            <Box
                sx={{
                    display: { xs: 'flex', sm: 'none' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 2,
                    position: 'relative',
                }}
            >
                <ToggleButtonGroup
                        value={timeRange}
                        exclusive
                        onChange={handleTimeRangeChange}
                        size="small"
                        disabled={isLoading}
                        sx={{
                            border: '1px solid #333333',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            '& .MuiToggleButtonGroup-grouped': {
                                margin: 0,
                                border: 0,
                                borderRadius: 0,
                                borderRight: `1px solid #333333`,
                                '&:last-of-type': {
                                    borderRight: 0,
                                },
                            },
                        }}
                    >
                        <ToggleButton 
                            value="1W" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1W')}
                        </ToggleButton>
                        <ToggleButton 
                            value="1M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="3M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.3M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="6M" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.6M')}
                        </ToggleButton>
                        <ToggleButton 
                            value="1Y" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.1Y')}
                        </ToggleButton>
                        <ToggleButton 
                            value="ALL" 
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                minWidth: '50px',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                bgcolor: 'transparent',
                                '&.Mui-selected': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 73, 0, 0.1)',
                                },
                            }}
                        >
                            {t('dashboard.balanceHistory.timeframes.ALL')}
                        </ToggleButton>
                    </ToggleButtonGroup>
            </Box>
        </Box>
    );
};

export default BalanceHistoryChart;
